import React from 'react';
import { AuthProvider, useAuth } from 'react-oidc-context';
import {
  startProactiveRenewal,
  stopProactiveRenewal,
  userManager,
} from '../../config/oidc';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any | null;
  login: () => void;
  logout: () => void;
  getAccessToken: () => string | null;
}

const AuthContext = React.createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProviderWrapper');
  }
  return context;
};

interface AuthProviderWrapperProps {
  children: React.ReactNode;
}

const AuthProviderWrapper: React.FC<AuthProviderWrapperProps> = ({ children }) => {
  const auth = useAuth();

  // Clean up OAuth query params from URL after successful authentication.
  React.useEffect(() => {
    if (auth.isAuthenticated && !auth.isLoading) {
      const url = new URL(window.location.href);
      const oauthParams = ['state', 'code', 'session_state', 'iss'];
      const hasOAuthParams = oauthParams.some((p) => url.searchParams.has(p));
      if (hasOAuthParams) {
        oauthParams.forEach((p) => url.searchParams.delete(p));
        window.history.replaceState({}, document.title, url.pathname + url.hash);
        console.log('[oidc] cleaned OAuth params from URL');
      }
    }
  }, [auth.isAuthenticated, auth.isLoading]);

  // Toggle the singleton renewal driver based on auth state. The timer
  // itself lives in config/oidc.ts at module scope so HMR / StrictMode
  // can't end up with two timers racing for the refresh token.
  React.useEffect(() => {
    if (auth.isAuthenticated) {
      startProactiveRenewal();
    } else {
      stopProactiveRenewal();
    }
    return () => {
      stopProactiveRenewal();
    };
  }, [auth.isAuthenticated]);

  // Pure observability: surface oidc-client-ts events so they're easy to
  // spot in DevTools. None of these handlers trigger renewal.
  React.useEffect(() => {
    const onUserLoaded = () => {
      console.log('[oidc] user loaded event (token store updated)');
    };
    const onSilentRenewError = (error: Error) => {
      console.error('[oidc] silent renew error event:', error);
    };
    userManager.events.addUserLoaded(onUserLoaded);
    userManager.events.addSilentRenewError(onSilentRenewError);
    return () => {
      userManager.events.removeUserLoaded(onUserLoaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
    };
  }, []);

  const login = React.useCallback(() => {
    auth.signinRedirect();
  }, [auth]);

  const logout = React.useCallback(() => {
    stopProactiveRenewal();
    const logoutUri = `${window.location.origin}${import.meta.env.BASE_URL}`;
    auth.signoutRedirect({ post_logout_redirect_uri: logoutUri });
  }, [auth]);

  const getAccessToken = React.useCallback(() => {
    return auth.user?.access_token || null;
  }, [auth.user]);

  const contextValue: AuthContextType = React.useMemo(
    () => ({
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
      user: auth.user,
      login,
      logout,
      getAccessToken,
    }),
    [auth.isAuthenticated, auth.isLoading, auth.user, login, logout, getAccessToken],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

interface AppAuthProviderProps {
  children: React.ReactNode;
}

const AppAuthProvider: React.FC<AppAuthProviderProps> = ({ children }) => {
  return (
    <AuthProvider userManager={userManager}>
      <AuthProviderWrapper>{children}</AuthProviderWrapper>
    </AuthProvider>
  );
};

export default AppAuthProvider;
